// src/hooks/useTokenRefresh.ts
// Proactive token refresh — calls /auth/refresh 1 minute BEFORE access token expires
// Prevents the user from ever hitting a 401 during normal usage

import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { setToken, logout } from 'store/slices/authSlice';
import { authService } from 'services/authService';

const ONE_MINUTE_MS  = 60 * 1000;
const ACCESS_TTL_MS  = 24 * 60 * 60 * 1000;   // 24 hours (must match backend ACCESS_TTL_SECS)
// Refresh 1 minute before expiry
const REFRESH_BEFORE = ONE_MINUTE_MS;
const REFRESH_AFTER  = ACCESS_TTL_MS - REFRESH_BEFORE;

export function useTokenRefresh(): void {
  const dispatch     = useDispatch();
  const { isAuthenticated, accessToken } = useSelector((s: RootState) => s.auth);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      try {
        const { accessToken: newToken } = await authService.refresh();
        dispatch(setToken(newToken));
        // Schedule the next refresh after getting the new token
        scheduleRefresh();
      } catch {
        // Refresh failed — refresh token also expired or was revoked
        dispatch(logout());
      }
    }, REFRESH_AFTER);
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Decode token to get exact expiry time
    try {
      const [, payloadB64] = accessToken.split('.');
      const payload = JSON.parse(atob(payloadB64));
      const expiresAt = payload.exp * 1000;   // convert to ms
      const now       = Date.now();
      const msUntilExpiry = expiresAt - now;

      // How long until we should proactively refresh (1 min before expiry)
      const msUntilRefresh = Math.max(0, msUntilExpiry - REFRESH_BEFORE);

      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(async () => {
        try {
          const { accessToken: newToken } = await authService.refresh();
          dispatch(setToken(newToken));
          scheduleRefresh();   // keep the cycle going
        } catch {
          dispatch(logout());
        }
      }, msUntilRefresh);

    } catch {
      // Malformed token — fall back to fixed interval
      scheduleRefresh();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, accessToken, dispatch, scheduleRefresh]);
}
